import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { Flip } from "gsap/Flip";
import { Draggable } from "gsap/Draggable";
import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode, type RefObject } from "react";
import { flushSync } from "react-dom";
import { Button, Dialog, DialogTrigger, Modal, ModalOverlay } from "react-aria-components";
import deckBack from "@/assets/figma/component-bar/deck-back.svg";
import deckMiddle from "@/assets/figma/component-bar/deck-middle.svg";
import expandIcon from "@/assets/figma/component-bar/expand.svg";
import toolsIcon from "@/assets/figma/component-bar/tools.svg";
import { MerryButton } from "@/shared/ui/MerryButton";
import { moveMerchantRailItem, reconcileMerchantRailOrder } from "./merchantConversationRailOrder";

gsap.registerPlugin(Draggable, Flip, useGSAP);

type RailMode = "stacked" | "expanded";

// 仅复用玻璃视图壳；真卡、查询与业务动作仍由正式商户入口拥有。
export function MerchantConversationRail({ content, compactRoot, cardIds }: {
  content: (mode?: RailMode, orderIds?: ReadonlyArray<string>) => ReactNode;
  compactRoot: RefObject<HTMLDivElement | null>;
  cardIds: string;
}) {
  const root = useRef<HTMLDivElement>(null);
  const pendingLayout = useRef<ReturnType<typeof Flip.getState> | null>(null);
  const [mode, setMode] = useState<RailMode>("stacked");
  const [compactOpen, setCompactOpen] = useState(false);
  const [itemOrderIds, setItemOrderIds] = useState(() => cardIds.split("|").filter(Boolean));
  const [isManagementVisible, setIsManagementVisible] = useState(false);
  const [announcement, setAnnouncement] = useState("");
  const managementLeaveTimer = useRef<number | null>(null);
  const modeRef = useRef(mode);
  const orderRef = useRef(itemOrderIds);
  const draggingItemId = useRef<string | null>(null);
  const incomingIds = cardIds.split("|").filter(Boolean);
  const resolvedOrder = reconcileMerchantRailOrder(itemOrderIds, incomingIds);
  const firstOrderedId = resolvedOrder[0] ?? "";
  modeRef.current = mode;
  orderRef.current = resolvedOrder;
  const viewportId = useId();
  const targets = () => root.current ? Array.from(root.current.querySelectorAll<HTMLElement>("[data-merchant-rail-motion]")) : [];
  const findItem = (id: string) => Array.from(root.current?.querySelectorAll<HTMLElement>("[data-merchant-rail-item]") ?? []).find(element => element.dataset.merchantRailItem === id);

  useEffect(() => {
    setItemOrderIds(previous => reconcileMerchantRailOrder(previous, cardIds.split("|").filter(Boolean)));
  }, [cardIds]);

  const cancelManagementLeave = () => {
    if (managementLeaveTimer.current !== null) window.clearTimeout(managementLeaveTimer.current);
    managementLeaveTimer.current = null;
  };
  const showManagement = () => {
    cancelManagementLeave();
    setIsManagementVisible(true);
  };
  const deferManagementLeave = () => {
    cancelManagementLeave();
    managementLeaveTimer.current = window.setTimeout(() => {
      managementLeaveTimer.current = null;
      if (!root.current?.contains(document.activeElement) && !draggingItemId.current) setIsManagementVisible(false);
    }, 220);
  };
  useEffect(() => () => cancelManagementLeave(), []);

  useGSAP(() => {
    const element = root.current;
    const previous = pendingLayout.current;
    pendingLayout.current = null;
    if (!element || !previous) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      element.dataset.merchantRailAnimating = "false";
      return;
    }
    element.dataset.merchantRailAnimating = "true";
    Flip.from(previous, {
      duration: 0.62, ease: "power3.inOut", scale: false, nested: true,
      onComplete: () => { element.dataset.merchantRailAnimating = "false"; },
      onInterrupt: () => { element.dataset.merchantRailAnimating = "false"; },
    });
  }, { dependencies: [mode], scope: root });

  useGSAP(() => {
    const stop = () => {
      const elements = targets();
      if (elements.length) {
        Flip.killFlipsOf(elements, true);
        gsap.set(elements, { clearProps: "transform,width,height,opacity,visibility,willChange" });
      }
      pendingLayout.current = null;
      if (root.current) root.current.dataset.merchantRailAnimating = "false";
    };
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)");
    const desktop = window.matchMedia("(min-width: 1440px)");
    const changeReduced = () => { if (reduced.matches) stop(); };
    const changeViewport = () => { stop(); setCompactOpen(false); };
    reduced.addEventListener("change", changeReduced);
    desktop.addEventListener("change", changeViewport);
    return () => {
      reduced.removeEventListener("change", changeReduced);
      desktop.removeEventListener("change", changeViewport);
      stop();
    };
  }, { scope: root });

  useGSAP(() => {
    const element = root.current;
    const viewport = element?.querySelector<HTMLElement>(".merchant-conversation-rail__viewport");
    if (!element || !viewport) return;
    const items = Array.from(element.querySelectorAll<HTMLElement>("[data-merchant-rail-item]"));
    const prefersReducedMotion = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const draggables = items.flatMap(item => {
      const id = item.dataset.merchantRailItem;
      const handle = item.querySelector<HTMLElement>("[data-merchant-rail-drag-handle]");
      if (!id || !handle) return [];
      return Draggable.create(item, {
        activeCursor: "grabbing",
        autoScroll: 1,
        cursor: "grab",
        dragClickables: true,
        force3D: true,
        minimumMovement: 4,
        trigger: handle,
        type: "y",
        onDragStart(this: Draggable) {
          draggingItemId.current = id;
          element.dataset.merchantRailDragging = id;
          showManagement();
          if (modeRef.current === "stacked") {
            modeRef.current = "expanded";
            flushSync(() => setMode("expanded"));
          }
          gsap.killTweensOf(item);
          gsap.set(item, { scale: 1.018, transformOrigin: "50% 50%", willChange: "transform", zIndex: 12 });
        },
        onDrag(this: Draggable) {
          const current = orderRef.current;
          if (!current.includes(id)) return;
          const targetRect = item.getBoundingClientRect();
          const targetCenter = targetRect.top + targetRect.height / 2;
          const remaining = current.filter(otherId => otherId !== id);
          let nextIndex = 0;
          for (const siblingId of remaining) {
            const sibling = findItem(siblingId);
            if (!sibling) continue;
            const siblingRect = sibling.getBoundingClientRect();
            if (targetCenter > siblingRect.top + siblingRect.height / 2) nextIndex += 1;
          }
          const next = [...remaining];
          next.splice(nextIndex, 0, id);
          if (current.every((candidate, index) => candidate === next[index])) return;
          const siblings = remaining.map(findItem).filter((candidate): candidate is HTMLElement => Boolean(candidate));
          Flip.killFlipsOf(siblings, true);
          const previous = prefersReducedMotion() ? null : Flip.getState(siblings);
          orderRef.current = next;
          flushSync(() => setItemOrderIds(next));
          // Draggable 保持当前指针的全局位置；换位后不可重设边界。
          this.update(false, true);
          if (previous) Flip.from(previous, { duration: 0.24, ease: "power2.out", prune: true, scale: false });
        },
        onDragEnd(this: Draggable) {
          const finalOrder = [...orderRef.current];
          const finish = () => {
            draggingItemId.current = null;
            delete element.dataset.merchantRailDragging;
            gsap.set(item, { clearProps: "transform,transformOrigin,willChange,zIndex" });
            this.update(true);
            const position = finalOrder.indexOf(id) + 1;
            setAnnouncement(position > 0 ? `本页已将商机组件移动到第 ${position} 位` : "本页组件顺序已更新");
          };
          if (prefersReducedMotion()) {
            gsap.set(item, { scale: 1, y: 0 });
            finish();
            return;
          }
          gsap.to(item, { duration: 0.24, ease: "power2.out", scale: 1, y: 0, onComplete: finish, onInterrupt: finish });
        },
      });
    });
    return () => {
      draggables.forEach(draggable => draggable.kill());
      if (items.length) {
        gsap.killTweensOf(items);
        gsap.set(items, { clearProps: "transform,transformOrigin,willChange,zIndex" });
      }
      draggingItemId.current = null;
      delete element.dataset.merchantRailDragging;
    };
  }, { dependencies: [cardIds], scope: root });

  useEffect(() => {
    const element = root.current;
    const list = element?.querySelector<HTMLElement>(".merchant-home-rail__cards");
    if (!element || !list) return;
    const first = list.querySelector<HTMLElement>("[data-merchant-rail-item]");
    const footer = list.querySelector<HTMLElement>(".merchant-home-rail__feedback");
    const measure = () => {
      // 测量真卡而非 Flip 临时改变尺寸的 wrapper；首卡过长时栏内滚动，不裁掉动作。
      const height = first ? (first.querySelector<HTMLElement>(".merchant-home-opportunity")?.getBoundingClientRect().height ?? first.offsetHeight) + (footer?.offsetHeight ? footer.offsetHeight + 12 : 0) : list.offsetHeight;
      element.style.setProperty("--merchant-rail-first-height", `${height}px`);
    };
    const observer = new ResizeObserver(measure);
    observer.observe(list);
    if (first) observer.observe(first.querySelector<HTMLElement>(".merchant-home-opportunity") ?? first);
    if (footer) observer.observe(footer);
    measure();
    return () => observer.disconnect();
  }, [cardIds, firstOrderedId]);

  const toggle = () => {
    const elements = targets();
    if (elements.length) {
      Flip.killFlipsOf(elements, true);
      pendingLayout.current = window.matchMedia("(prefers-reduced-motion: reduce)").matches ? null : Flip.getState(elements);
    }
    // 收起后不保留展开列表的旧滚动位置，否则第一卡会被留在滚动原点上方。
    root.current?.querySelector<HTMLElement>(".merchant-conversation-rail__viewport")?.scrollTo({ top: 0 });
    setMode(current => current === "stacked" ? "expanded" : "stacked");
  };

  const manage = () => {
    showManagement();
    if (modeRef.current === "stacked") toggle();
    window.requestAnimationFrame(() => root.current?.querySelector<HTMLElement>("[data-merchant-rail-drag-handle]")?.focus());
  };

  const handleMoveKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "ArrowUp" && event.key !== "ArrowDown") return;
    const handle = event.target instanceof HTMLElement
      ? event.target.closest<HTMLElement>("[data-merchant-rail-drag-handle]")
      : null;
    const id = handle?.dataset.merchantRailDragHandle;
    if (!id) return;
    event.preventDefault();
    showManagement();
    if (modeRef.current === "stacked") {
      toggle();
      setAnnouncement("组件栏已展开，请继续用上下方向键调整顺序");
      return;
    }
    const current = orderRef.current;
    const next = moveMerchantRailItem(current, id, event.key === "ArrowUp" ? "up" : "down");
    if (current.every((candidate, index) => candidate === next[index])) return;
    const elements = Array.from(root.current?.querySelectorAll<HTMLElement>("[data-merchant-rail-item]") ?? []);
    Flip.killFlipsOf(elements, true);
    const previous = window.matchMedia("(prefers-reduced-motion: reduce)").matches ? null : Flip.getState(elements);
    orderRef.current = next;
    flushSync(() => setItemOrderIds(next));
    if (previous) Flip.from(previous, { duration: 0.24, ease: "power2.out", prune: true, scale: false });
    setAnnouncement(`本页已将商机组件移动到第 ${next.indexOf(id) + 1} 位`);
  };

  return <>
    <div ref={root} className="merchant-home-rail__desktop merchant-conversation-rail" data-merchant-rail-mode={mode} data-merchant-rail-animating="false" data-merchant-rail-management-visible={isManagementVisible} onPointerEnter={showManagement} onPointerLeave={deferManagementLeave} onFocusCapture={showManagement} onBlurCapture={event => { if (!root.current?.contains(event.relatedTarget)) deferManagementLeave(); }} onKeyDownCapture={handleMoveKeyDown}>
      <div className="merchant-conversation-rail__management merry-component-bar__toolbar-surface" data-merchant-rail-management>
        <span aria-hidden="true" className="merry-component-bar__toolbar-glass" />
        <Button aria-label="管理组件顺序" className="merry-component-bar__toolbar-button" onPress={manage} excludeFromTabOrder={!isManagementVisible}>
          <img alt="" draggable="false" src={toolsIcon} />
        </Button>
        <span aria-hidden="true" className="merry-component-bar__toolbar-stroke" />
      </div>
      <div className="merchant-conversation-rail__toggle merry-component-bar__toolbar-surface">
        <span aria-hidden="true" className="merry-component-bar__toolbar-glass" />
        <Button aria-label={mode === "stacked" ? "展开组件栏" : "收起组件栏"} aria-expanded={mode === "expanded"} aria-controls={viewportId} className="merry-component-bar__toolbar-toggle" onPress={toggle}>
          <img alt="" draggable="false" src={expandIcon} />
        </Button>
        <span aria-hidden="true" className="merry-component-bar__toolbar-stroke" />
      </div>
      <div id={viewportId} className="merchant-conversation-rail__viewport" data-merchant-rail-motion>{content(mode, resolvedOrder)}</div>
      {cardIds && <div aria-hidden="true" className="merchant-conversation-rail__deck" data-merchant-rail-motion>
        <img alt="" draggable="false" src={deckMiddle} /><img alt="" draggable="false" src={deckBack} />
      </div>}
      <span className="sr-only" aria-live="polite" data-testid="merchant-conversation-rail-reorder-status">{announcement}</span>
    </div>
    <DialogTrigger isOpen={compactOpen} onOpenChange={setCompactOpen}>
      <Button aria-label="打开组件栏" className="merry-component-bar__compact-trigger merchant-home-rail__compact-trigger"><img alt="" draggable="false" src={toolsIcon} /></Button>
      <ModalOverlay isDismissable className="merchant-home-dialog-overlay"><Modal className="merchant-home-dialog"><Dialog aria-label="对话商机组件">{({ close }) => <>
        <h2>对话商机组件</h2><div ref={compactRoot} className="merchant-home-rail merchant-home-rail__compact-body">{content("expanded", resolvedOrder)}</div><MerryButton hierarchy="level1" onPress={close}>收起组件栏</MerryButton>
      </>}</Dialog></Modal></ModalOverlay>
    </DialogTrigger>
  </>;
}
